import {matchUrlOrigins} from './utils/utils';
import {getElement} from './utils/domManager';

/**
 * CM post message abstract server. It allows to message commands to be embedded in CM iframe parent
 * which react accordingly. E.g. set filter
 * @param hostUrl {URL} base host url of a iframe
 * @param id {string} iframe id
 * @returns {Object}
 */
const messageServer = (hostUrl, id) => {
    let listeners = {};

    const messageEventListener = (event) => {
        const { origin, data: { path, params }, source } = event;

        let iframeElement = undefined;
        try {
            iframeElement = getElement(id);
        } catch {}

        matchUrlOrigins(origin, hostUrl) && iframeElement && iframeElement?.contentWindow === source ?
            listeners[path]?.forEach((listener) => listener(...params)) :
            console.warn(`
                Message API: Incoming message is not send from trusted url or content window. Ignoring the message.
                Trusted url: ${hostUrl}, message received from: ${origin}.
                If the urls are same, you have probably more than one iframe with the same url rendered
                and the message was send from the different content window.
                ${hostUrl && hostUrl.toString().includes('localhost') &&
                    'Or you are in development environment. In the development environments there are some ignored dev tools messages'}
            `);
    };

    const start = () => window.addEventListener('message', messageEventListener);
    const stop = () => window.removeEventListener('message', messageEventListener);

    const subscribe = (type, listener) => {
        !Object.keys(listeners).length && start();
        listeners = Object.assign({}, listeners, { [type]: [listener].concat(listeners[type] ?? []) });
    };
    const unsubscribe = (type, listener) => {
        listeners = Object.assign({}, listeners, { [type]: listeners[type]?.filter(item => item !== listener) ?? [] });
        !listeners[type]?.length && delete listeners[type];
        !Object.keys(listeners).length && stop();
    };

    return {
        subscribe,
        unsubscribe
    };
};

/**
 * CM post message API server.
 * @param hostUrl {URL} base host url of a iframe
 * @param id {string} iframe id
 * @returns {Object}
 */
const messageServerController = (hostUrl, id) => {
    const {subscribe, unsubscribe} = messageServer(hostUrl, id);

    // Specific message api functions
    const addAddFilterListener = (listener) => subscribe('filters/add', listener);
    const removeAddFilterListener = (listener) => unsubscribe('filters/add', listener);
    const addSetFilterListener = (listener) => subscribe('filters/set', listener);
    const removeSetFilterListener = (listener) => unsubscribe('filters/set', listener);
    const addRemoveFilterListener = (listener) => subscribe('filters/remove', listener);
    const removeRemoveFilterListener = (listener) => unsubscribe('filters/remove', listener);
    const addEmbeddingReadyListener = (listener) => subscribe('embedding/ready', listener);

    return {
        addAddFilterListener,
        removeAddFilterListener,
        addSetFilterListener,
        removeSetFilterListener,
        addRemoveFilterListener,
        removeRemoveFilterListener,
        addEmbeddingReadyListener
    };
};

export default messageServerController;
